/**
 * @file 活动页 web 端入口
 *
 * @author aprilcai
 *
 */
import Vue, { type PluginObject } from 'vue';
import { mapState, mapActions } from 'vuex';
import VueLazyLoad from 'vue-lazyload';
import VueCompositionApi from '@vue/composition-api';
import { initActContext } from '@tencent/iwan-act-comps-sdk';
import OpenUrlPlugin from '@tencent/iwan-act-plugin-act-openurl';
import { getUrlParam, tvLogOverwrite } from '@tencent/magic-ui-util';
import env from '@tencent/vft-common/env';
import requestPlugin from '@tencent/magic-ui-plugin-vip-request';
import sharePlugin from '@tencent/tvf-jsapi-comp-share';
import TvfJsapi from '@tencent/tvf-jsapi';

import '@/web/src/util/pre-handler';
import '@/web/src/util/performance-report';
import { tryToLoadDebugger } from '@/web/src/util/debugger';
import fetch from '@/web/src/util/fetch';

import ActUtils from '@/web/src/inner-plugins/act-utils-plugin';

import { registerSvelteComponent, initActCompContextInterface } from '@/svelte-comp-adapter';

import MagicUi, { video, pop, toast, notifyPop } from '@/ui';

import App from '@/web/src/App.vue';
import store from '@/web/src/store';

import Focusable from '@/web/src/components/Focusable.vue';
import QrCode from '@/web/src/components/QrCode.vue';
import vueAlert from '@/web/src/components/vue-alert';
import vueConfirm from '@/web/src/components/vue-confirm';
import { isDevComp, registerDevComp } from '@/web/src/dev-comp';

import { StageReportStage } from '@/shared/constant';

import imports, { components as c, plugins as p, svelteComponents } from './comp-entry.js';

import '@tencent/vft-style/src/reset.scss';
import '@/ui/src/style.scss';

tryToLoadDebugger(Vue);

// #region Vue.prototype
Vue.prototype.toast = toast;
Vue.prototype.alert = vueAlert;
Vue.prototype.tvfJsapi = TvfJsapi;
Vue.prototype.closeCurrentPage = () => {
  TvfJsapi.invoke('close');
};
Vue.prototype.confirm = vueConfirm;
// web端，不在编辑器中
Vue.prototype.inMagic = false;
// web端，非 hippy
Vue.prototype.inHippy = false;
// web端，非 hippy
Vue.prototype.inHippyWeb = false;
// 预渲染标记
Vue.prototype.inPrerender = false;
Vue.config.productionTip = false;
Vue.prototype.fetch = fetch;
env.isTv = getUrlParam('_device') === 'tv';
env.engine = 'web';
Vue.prototype.env = env;
window.env = env;
// #endregion Vue.prototype

tvLogOverwrite({ env, type: 'web', Vue });

// #region inner plugins
Vue.use(ActUtils, { env: 'test' });
Vue.use(VueCompositionApi);
Vue.use(MagicUi as PluginObject<{}>);
Vue.use(OpenUrlPlugin);
Vue.use(VueLazyLoad);
Vue.use(requestPlugin);
Vue.use(sharePlugin);
// #endregion inner plugins

// #region inner Components
Vue.component(video.name, video);
Vue.component(pop.name, pop);
Vue.component(notifyPop.name, notifyPop);
Vue.component(Focusable.name, Focusable);
Vue.component(QrCode.name, QrCode);
// #endregion inner Components

const plugins: Array<PluginObject<any>> = p;

// 在编译时会处理
// eslint-disable-next-line
const components: Array<Record<any, any>> = c;

// 注册 svelte 组件
svelteComponents.forEach(({ name, loader }: any) => {
  registerSvelteComponent(name, loader);
});

const stageReport = window.stageReport || function () {};

Promise.all(imports)
  .then(async () => {
    stageReport(StageReportStage.PLUGIN_LOADED);

    // 本地组件调试的入口
    if (isDevComp()) {
      await registerDevComp(MagicUi);
    }

    // 初始化 mappingInfo
    initActContext({
      mappingInfo: window.mappingInfo,
    });

    plugins.forEach((plugin: PluginObject<{}>) => {
      Vue.use(plugin);
    });

    const existLoginPlugin = plugins.find(p => p.pluginType === 'login');
    if (!existLoginPlugin) {
      console.warn('[main]业务未使用登录插件');
    }

    // 如果有上报插件，执行上报插件的初始化方法
    const existReportPlugin = plugins.find(p => p.pluginType === 'report');
    if (existReportPlugin) {
      Vue.prototype.$magicReport?.init?.();
    }

    Vue.mixin({
      computed: {
        ...mapState(['userInfo', 'payVip', 'isLogin', 'loginReady']),
        hasCheckLogin() {
          console.warn('[deprecated] hasCheckLogin已启用，请使用loginReady替代');
          return store.state.hasCheckLogin;
        },
      },
      methods: {
        ...mapActions(['checkLogin', 'getUserInfo', 'getPayVip', 'logout']),
        openLogin(...args) {
          return Vue.prototype.$magicLogin.openLogin(...args);
        },
        $openLogin(...args) {
          return Vue.prototype.$magicLogin.openLogin(...args);
        },
        switchLogin(...args) {
          return Vue.prototype.$magicLogin.switchLogin(...args);
        },
      },
    });

    window.$?.(() => {
      window.magicApp = new Vue({
        store,
        created() {
          // 初始化 contextInterface
          initActCompContextInterface(Vue, this);
        },
        mounted() {
          Vue.prototype.$bus?.$emit('magic:root:events:mounted', window.magicApp);
          stageReport(StageReportStage.APP_MOUNTED);
        },
        render: h => h(App),
      }).$mount('#app', false);
    });
  })
  .catch((e) => {
    stageReport(StageReportStage.PLUGIN_LOAD_ERROR);
    if (e.name === 'ChunkLoadError') {
      const v = new Vue();
      v.confirm({
        title: '提示',
        msg: '页面资源加载失败，刷新重试。',
        confirm: () => {
          location.reload();
        },
      });
    }
  });
